import { useState } from 'react';

import { endOfMonth, format, startOfMonth, subDays, subMonths } from 'date-fns';
import { Calendar, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface LynAIDateFilterProps {
  startDate: string | null;
  endDate: string | null;
  onDateChange: (startDate: string | null, endDate: string | null) => void;
}

type PresetOption = {
  label: string;
  getValue: () => { start: string; end: string };
};

const presets: PresetOption[] = [
  {
    label: 'Last 7 days',
    getValue: () => ({
      start: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Last 30 days',
    getValue: () => ({
      start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Last 90 days',
    getValue: () => ({
      start: format(subDays(new Date(), 90), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'This month',
    getValue: () => ({
      start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
  {
    label: 'Last month',
    getValue: () => {
      const lastMonth = subMonths(new Date(), 1);
      return {
        start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
      };
    },
  },
  {
    label: 'Last 6 months',
    getValue: () => ({
      start: format(subMonths(new Date(), 6), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    }),
  },
];

const LynAIDateFilter = ({ startDate, endDate, onDateChange }: LynAIDateFilterProps) => {
  const [localStartDate, setLocalStartDate] = useState(startDate || '');
  const [localEndDate, setLocalEndDate] = useState(endDate || '');

  const handlePresetClick = (preset: PresetOption) => {
    const { start, end } = preset.getValue();
    setLocalStartDate(start);
    setLocalEndDate(end);
    onDateChange(start, end);
  };

  const handleApply = () => {
    onDateChange(localStartDate || null, localEndDate || null);
  };

  const handleClear = () => {
    setLocalStartDate('');
    setLocalEndDate('');
    onDateChange(null, null);
  };

  const hasFilter = startDate || endDate;
  const displayText = hasFilter ? `${startDate || 'Start'} to ${endDate || 'End'}` : 'All time';

  return (
    <div className='flex items-center gap-2'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='outline' size='sm' className='flex items-center gap-2'>
            <Calendar className='h-4 w-4' />
            <span className='hidden sm:inline'>{displayText}</span>
            <span className='sm:hidden'>Date</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-72'>
          {/* Presets */}
          <div className='p-2'>
            <p className='text-xs font-medium text-muted-foreground mb-2'>Quick select</p>
            <div className='grid grid-cols-2 gap-1'>
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  variant='ghost'
                  size='sm'
                  className='justify-start text-xs h-7'
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          <DropdownMenuSeparator />

          {/* Custom date inputs */}
          <div className='p-2 space-y-3'>
            <p className='text-xs font-medium text-muted-foreground'>Custom range</p>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <Label htmlFor='start-date' className='text-xs'>
                  Start
                </Label>
                <Input
                  id='start-date'
                  type='date'
                  value={localStartDate}
                  onChange={(e) => setLocalStartDate(e.target.value)}
                  className='h-8 text-xs'
                />
              </div>
              <div>
                <Label htmlFor='end-date' className='text-xs'>
                  End
                </Label>
                <Input
                  id='end-date'
                  type='date'
                  value={localEndDate}
                  onChange={(e) => setLocalEndDate(e.target.value)}
                  className='h-8 text-xs'
                />
              </div>
            </div>
            <div className='flex gap-2'>
              <Button variant='outline' size='sm' className='flex-1' onClick={handleClear}>
                Clear
              </Button>
              <Button size='sm' className='flex-1' onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {hasFilter && (
        <Button variant='ghost' size='sm' onClick={handleClear} className='h-8 w-8 p-0'>
          <X className='h-4 w-4' />
        </Button>
      )}
    </div>
  );
};

export default LynAIDateFilter;
